import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { Button } from '@common/components/Button';
import SectionContainer from '@common/components/SectionContainer';
import { SectionHero } from '@common/components/SectionHero';
import { Spinner } from '@common/components/Spinner/Spinner';
import { Routes } from '@common/constants';
import { analytics } from '@features/analytics';
import { useGetRestaurants } from '@features/tables/hooks/useGetRestaurants';
import { useGetRestaurantConfig } from '@features/tables/hooks/useGetRestaurantConfig';
import { useGetTablesWeekAvailability } from '@features/tables/hooks/useGetTablesWeekAvailability';
import { useTablesSettings } from '@features/tables/hooks/useGetTablesSettings';
import { useGetSpaSettings } from '@features/spa';
import { useTranslations } from '@lang';
import RestaurantBox from '@features/tables/pages/Tables/components/RestaurantBox';
import SpaStickyBar from '@features/tables/pages/Tables/components/StickyBar';
import TableConfigurationWizard from '@features/tables/pages/Tables/components/TableConfigurationWizard';
import { TableForm } from '@features/tables/pages/Tables/components/TableForm';
import { getDatesRange } from '@utils/helpers';
import Cookie from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { Router, useRouter } from 'shared-routing';
import { getDisabledDates } from './TablesContainer.helpers';
import { ButtonWrapper } from './TablesContainer.styles';
import { TablesContainerProps, TablesContainerReduxProps } from './TablesContainer.types';

export const TablesContainer: React.FC<TablesContainerProps & TablesContainerReduxProps> = ({
  basketDetails = null,
  isResult = false,
  openModal = false,
  openModalWith = '',
  journeyParams,
  language,
  property,
  restaurant,
  tablesPage,
  tableFormStartDate,
  formFieldChange,
  formReset,
  createBasket,
  updateBasket,
  destroy,
}) => {
  const { t } = useTranslations();

  const {
    data: restaurants = null,
    isLoading: isLoadingRestaurants,
    error: restaurantsError,
  } = useGetRestaurants(property.id, language);

  const currentRestaurant = React.useMemo(() => {
    return (restaurants || []).find(
      (rest) => rest.restaurantName === restaurant?.value || rest.value === restaurant?.value
    );
  }, [restaurants, restaurant]);

  const closedDates = currentRestaurant?.closedDates || { closedDays: [] };

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const { fromDate, tableAdults, tableChildren, adults, children } = journeyParams;

  const router = useRouter();
  const [isBasketSucceeded, setIsBasketSucceeded] = useState(false);
  const [guestsInputChanged, setGuestInputChanged] = useState(false);
  const [guestsInputClosed, setGuestInputClosed] = useState(false);

  const ID = 'tables-wrapper';
  const startDate = tableFormStartDate || fromDate;
  const initialAdults = tableAdults ?? adults;
  const initialChildren = tableChildren ?? children;

  const [selectedRestaurant, setSelectedRestaurant] = useState<{
    restaurantName: string;
    restaurantId: number;
  } | null>(() => {
    // Initialize with current restaurant if available
    const current = (restaurants || []).find(
      (rest) => rest.restaurantName === restaurant?.value || rest.value === restaurant?.value
    );
    if (current && current.restaurantName && current.config?.id) {
      return {
        restaurantName: current.restaurantName,
        restaurantId: current.config.id,
      };
    }
    return null;
  });

  const [guestCounts, setGuestCounts] = useState({
    adults: initialAdults || 2,
    children: initialChildren || 0,
  });

  const {
    data: weekAvailabilityData = {},
    isLoading: isLoadingWeekAvailability,
    isError: weekAvailabilityError,
  } = useGetTablesWeekAvailability({
    propertyId: property.id,
    startDate: startDate ? startDate.toString() : new Date().toISOString(),
    restaurant: selectedRestaurant?.restaurantName || '',
    restaurantId: selectedRestaurant?.restaurantId,
    adults: guestCounts.adults,
    children: guestCounts.children,
    enabled:
      openModal && !!selectedRestaurant?.restaurantName && !!selectedRestaurant?.restaurantId,
  });

  const {
    data: restaurantConfig,
    isFetching: isLoadingRestaurantConfig,
    error: restaurantConfigError,
  } = useGetRestaurantConfig(
    property.id,
    startDate ? startDate.toString() : new Date().toISOString(),
    selectedRestaurant?.restaurantName || '',
    !!selectedRestaurant?.restaurantName
  );

  const { data: tablesSettings } = useTablesSettings();
  // Fallback for maxOnlinePartySize when API returns null
  const maxPartyFallback =
    (tablesSettings?.data?.defaultParty?.adults ?? 2) +
    (tablesSettings?.data?.defaultParty?.children ?? 0);

  const dates = getDatesRange(startDate);

  // Convert week availability data to the format expected by the form
  const timeSlots = Object.entries(weekAvailabilityData).map(([date, data]) => ({
    date,
    data: data || {
      timeSlots: [],
      promotions: [],
      standardAvailabilityMayRequireCreditCard: false,
    },
  }));

  // Collect all unique promotions from all dates
  const promotions = Object.values(weekAvailabilityData).reduce(
    (allPromotions, data) => {
      if (data?.promotions) {
        data.promotions.forEach((promotion) => {
          if (!allPromotions.some((p) => p.id === promotion.id)) {
            allPromotions.push(promotion);
          }
        });
      }
      return allPromotions;
    },
    [] as {
      id: string | number;
      name: string;
    }[]
  );

  const disabledDates = getDisabledDates(dates, closedDates, restaurantConfig, timeSlots);
  const mergedDatesDisabled = dates.map(({ date }) => ({
    date,
    disableDate: disabledDates[date.toString()],
  }));

  const hasRestaurants = restaurants && restaurants.length > 0;

  const initialValues = {
    guests: {
      adults: String(initialAdults || 2),
      children: String(initialChildren || 0),
    },
    visitDate: mergedDatesDisabled.find((date) => !date.disableDate)?.date,
    restaurant: openModalWith || restaurant || (hasRestaurants ? restaurants[0].value : ''),
    startDate,
  };

  useEffect(() => {
    if (currentRestaurant && openModal) {
      setSelectedRestaurant({
        restaurantName: currentRestaurant.restaurantName,
        restaurantId: currentRestaurant.config?.id,
      });
    }
  }, [currentRestaurant, openModal]);

  useEffect(() => {
    if (initialAdults !== undefined && initialChildren !== undefined) {
      setGuestCounts({
        adults: initialAdults || 2,
        children: initialChildren || 0,
      });
    }
  }, [initialAdults, initialChildren]);

  const handleGuestsInputClose = () => {
    setGuestInputClosed(true);
    setGuestInputChanged(false);
  };

  const handleGuestsInputOpen = () => {
    setGuestInputClosed(false);
  };

  const getWeekOfAvailability = (data) => {
    const selectedRestaurantFromData = (restaurants || []).find(
      (rest) =>
        rest.restaurantName === data.restaurant?.value || rest.value === data.restaurant?.value
    );
    if (selectedRestaurantFromData) {
      setSelectedRestaurant({
        restaurantName: selectedRestaurantFromData.restaurantName,
        restaurantId: selectedRestaurantFromData.config?.id,
      });
      if (data.guests) {
        setGuestCounts({
          adults: parseInt(data.guests.adults, 10) || 2,
          children: parseInt(data.guests.children, 10) || 0,
        });
      }
    }
  };

  const restaurantName = restaurant
    ? restaurants?.find((restaurantObject) => restaurantObject.value === restaurant)?.label
    : '';
  const heroBackgroundUrl = tablesPage?.heroBackgroundImage?.url;

  const handleModalOpen = (restaurant = '') => {
    if (hasRestaurants && restaurants.length === 1 && restaurants[0].restaurantName) {
      restaurant = restaurants[0].restaurantName;
    }
    const route = restaurant
      ? {
          pathname: Routes.TABLES_BOOK_RESTAURANT,
          query: { restaurant },
        }
      : {
          pathname: Routes.TABLES_BOOK,
        };

    router.push(route);
  };

  const handleModalClose = () => {
    router.push({ pathname: Routes.TABLES_HOME });
  };

  const hideSuccess = () => {
    setIsBasketSucceeded(false);
    formReset('tableForm');
    formFieldChange('tableForm', 'restaurant', null);
  };

  const showSuccess = () => {
    setIsBasketSucceeded(true);
    handleModalClose();
  };

  const addToBasket = async (data): Promise<void> => {
    const basketId = Cookie.get('basketId');
    const restaurant = (restaurants || []).find(
      (r) => r.restaurantName === data.restaurant?.value || r.value === data.restaurant?.value
    );
    const resturantLabel = restaurant?.config ? restaurant.config.name : restaurant?.label;
    const tableItem = {
      type: 'tables',
      adults: parseInt(data.guests.adults, 10),
      children: parseInt(data.guests.children, 10),
      note: data.note,
      dietaryRequirements: data.dietaryRequirements,
      celebrations: data.celebrationOptions,
      bookable: {
        ids: {
          timeslot: data.timeslot,
          restaurant: restaurant?.restaurantName,
          restaurantName: resturantLabel,
          promotionId: data.promotion?.id,
          restaurantConfig: restaurant?.config.id,
          timeslotReservationId:
            data.promotion?.timeslotReservationId ?? data.timeslotReservationId,
        },
      },
    };

    const payload: { items: unknown[] } = {
      items: [tableItem],
    };

    if (basketId) {
      const existingItems = basketDetails?.items || [];
      payload.items = [...payload.items, ...existingItems];
      await updateBasket(property.id, basketId, language, payload);
    } else {
      await createBasket(property.id, language, payload);
    }
    showSuccess();
  };

  useEffect(() => {
    if (openModal && startDate)
      getWeekOfAvailability({ ...initialValues, visitDate: startDate, restaurant });
  }, [openModal, startDate, restaurant]);

  useEffect(() => {
    if (guestsInputChanged && guestsInputClosed) {
      getWeekOfAvailability({ ...initialValues, visitDate: startDate, restaurant });
      setGuestInputChanged(false);
    }
  }, [guestsInputChanged, guestsInputClosed]);

  const showBasketDrawer = isBasketSucceeded && basketDetails?.items.length;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  const isError = restaurantsError || weekAvailabilityError || restaurantConfigError;

  useEffect(() => {
    if (isError) {
      Router.push({ pathname: Routes.TABLES_HOME });
    }
  }, [isError]);

  return (
    <>
      {showBasketDrawer && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
          open={isBasketSucceeded}
          onClose={hideSuccess}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          items={basketDetails?.items}
          modules={modulesToRecommend}
          hideArrivalTimes={hideArrivalTimes}
        />
      )}

      <Spinner
        isActive={isLoadingRestaurants || isLoadingRestaurantConfig || isLoadingWeekAvailability}
      />

      <TableConfigurationWizard
        isModalOpen={openModal}
        destroyForm={destroy}
        handleClose={handleModalClose}
        restaurantName={restaurantName}
      >
        {openModal && restaurants && (
          <TableForm
            initialValues={initialValues}
            checkAvailability={getWeekOfAvailability}
            fetchRestaurantConfig={(data) => {
              // Trigger week availability refetch with updated data
              getWeekOfAvailability({ ...data, visitDate: startDate });
            }}
            onSubmit={addToBasket}
            restaurants={restaurants}
            timeSlots={timeSlots}
            promotions={promotions}
            formFieldChange={formFieldChange}
            dates={mergedDatesDisabled}
            timeslotError={weekAvailabilityError}
            restaurantConfig={restaurantConfig}
            maxPartyFallback={maxPartyFallback}
            restaurant={restaurant}
            id={ID}
            tablesDisableChildBookings={
              property?.modulesExcludingDisabled?.tables?.features?.disableChildBookings ?? false
            }
            onGuestsInputClose={handleGuestsInputClose}
            onGuestsInputChanged={() => setGuestInputChanged(true)}
            onGuestsInputOpen={handleGuestsInputOpen}
          />
        )}
      </TableConfigurationWizard>

      {!isResult && (
        <SectionHero
          backgroundUrl={heroBackgroundUrl || ''}
          summary={tablesPage?.content || ''}
          title={tablesPage?.title || ''}
          renderButton={
            <ButtonWrapper>
              <Button
                isPrimary
                onClick={() => handleModalOpen()}
                disabled={!hasRestaurants || !!restaurantsError}
              >
                {t('tables.bookTable')}
              </Button>
            </ButtonWrapper>
          }
        />
      )}
      <SectionContainer>
        {!isLoadingRestaurants && hasRestaurants && (
          <>
            {restaurants.map(({ value, label, config }, index) => {
              const parsedConfig = config || {};
              return (
                <RestaurantBox
                  key={value}
                  image={parsedConfig?.imageGallery?.length && parsedConfig.imageGallery[0].url}
                  subtitle={parsedConfig.subtitle}
                  title={parsedConfig.name || label}
                  description={parsedConfig.description}
                  menu={parsedConfig.menu}
                  isReversed={index % 2 === 1}
                  handleAddClick={() => {
                    analytics.track({
                      event: 'view_item',
                      params: {
                        items: [{ item_category: 'tables', item_id: value, item_name: label }],
                      },
                    });

                    handleModalOpen(value);
                  }}
                />
              );
            })}
          </>
        )}
      </SectionContainer>
      <SpaStickyBar restaurant={restaurantName} id={ID} isModalOpen={openModal} />
    </>
  );
};
